import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { api } from "../api/client";
import { IconMail, IconAlert, IconLoader, IconCheck } from "../components/Icons";
import LanguageFlags from "../components/LanguageFlags";
import logoUrl from "/logo-login.png";

declare global {
  interface Window {
    turnstile?: {
      render: (el: HTMLElement, opts: { sitekey: string; callback: (token: string) => void }) => string;
      reset: (widgetId: string) => void;
    };
  }
}

const TURNSTILE_SRC = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";

export default function ForgotPassword() {
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [siteKey, setSiteKey] = useState<string | null>(null);
  const tokenRef = useRef<string | null>(null);
  const widgetRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // The site key is public runtime config served by the backend.
    api.get("/auth/public-config")
      .then(({ data }) => setSiteKey(data.turnstile_site_key ?? null))
      .catch(() => setSiteKey(null));
  }, []);

  useEffect(() => {
    if (!siteKey || !widgetRef.current) return;
    const render = () => {
      if (window.turnstile && widgetRef.current) {
        window.turnstile.render(widgetRef.current, {
          sitekey: siteKey,
          callback: (token) => { tokenRef.current = token; },
        });
      }
    };
    if (window.turnstile) {
      render();
      return;
    }
    const script = document.createElement("script");
    script.src = TURNSTILE_SRC;
    script.async = true;
    script.onload = render;
    document.head.appendChild(script);
  }, [siteKey]);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setErr(null);
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", {
        email,
        turnstile_token: tokenRef.current,
      });
      setSent(true);
    } catch (e: any) {
      setErr(e?.response?.data?.detail || t("auth.forgotError"));
    } finally {
      setLoading(false);
    }
  }

  return (
    <div style={{
      minHeight: "100vh", display: "flex", alignItems: "center",
      justifyContent: "center", background: "var(--bg)", padding: 24,
    }}>
      <div style={{ position: "fixed", top: 16, right: 16 }}>
        <LanguageFlags />
      </div>
      <div style={{ width: "100%", maxWidth: 400 }}>
        <div style={{ display: "flex", justifyContent: "center", marginBottom: 28 }}>
          <img src={logoUrl} alt="LuigiPOS" style={{ maxWidth: 160, height: "auto" }} />
        </div>
        <div className="card card-glow" style={{ padding: 32 }}>
          <h1 style={{ fontSize: 22, fontWeight: 700, marginBottom: 4 }}>{t("auth.forgotTitle")}</h1>
          <p style={{ color: "var(--text-secondary)", fontSize: 14, marginBottom: 24 }}>
            {t("auth.forgotSubtitle")}
          </p>

          {sent ? (
            <div style={{
              display: "flex", alignItems: "flex-start", gap: 8,
              padding: "12px 16px", borderRadius: "var(--radius-sm)",
              background: "var(--green-dim)", color: "var(--green)", fontSize: 14,
            }}>
              <IconCheck size={18} style={{ flexShrink: 0, marginTop: 1 }} />
              {t("auth.forgotSent")}
            </div>
          ) : (
            <form onSubmit={submit} style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              <div>
                <label style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  {t("auth.email")} <IconMail size={16} className="muted" />
                </label>
                <input
                  type="email" placeholder="admin@firma.cz" value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>

              {siteKey && <div ref={widgetRef} />}

              {err && (
                <div style={{
                  display: "flex", alignItems: "center", gap: 8,
                  padding: "10px 14px", borderRadius: "var(--radius-sm)",
                  background: "var(--red-dim)", color: "var(--red)", fontSize: 13,
                }}>
                  <IconAlert size={16} />
                  {err}
                </div>
              )}

              <button type="submit" className="primary" disabled={loading} style={{ marginTop: 4 }}>
                {loading ? <IconLoader size={18} /> : t("auth.sendResetLink")}
              </button>
            </form>
          )}

          <p style={{ marginTop: 24, textAlign: "center", color: "var(--text-secondary)", fontSize: 14 }}>
            <Link to="/login" style={{ color: "var(--accent)", fontWeight: 600 }}>{t("auth.backToLogin")}</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
